import { useState } from 'react';
import {
    Box,
    Card,
    Divider,
    FlexBox,
    FluidImage,
    Li,
    Link,
    Spinner,
    Typography,
    Ul,
    useScreenClass,
} from '@vp/swan';
import { Currency, bugtracker, price } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { expertServiceToProduct } from '../BriefContext';
import { WorkEntityDisplay } from '../BriefForm';
import { BriefProductFragment, BriefRequestFragment } from '../BriefForm/brief.generated';
import { ProductModal } from '../ProductModal';
import { Agreement, SOWModal } from './SOWModal';
import { useProductImageUrlQuery } from './productImageUrl.generated';

export interface Product {
    title: BriefProductFragment['title'];
    details: Array<{
        __typename: 'ProductPackageDetails';
        title: string;
        description?: string;
    }>;
    additionalDetails?: Array<{
        __typename: 'ProductPackageDetails';
        title: string;
        description: string;
    }>;
    pricing: Omit<BriefProductFragment['pricing'], 'earningsInUsd'> & { earningsInUsd?: number };
    fulfilmentMpvId?: BriefProductFragment['fulfilmentMpvId'];
    description?: string;
}

export enum AgreementType {
    Standard = 'standard',
    Swift = 'swift',
}

/**
 * Always include tax in prices shown in the ProductCard
 * Currently the product card is only used in Matching and Collaboration
 * Defaults to true to match existing behaviour
 */
const INCLUDE_TAX = true;

export interface ProductCardProps {
    product?: Product;
    request?: BriefRequestFragment;
    withProductModal?: boolean;
    hideImage?: boolean;
    isFreelanceDesigner?: boolean;
    matchedDate?: string;
    fullWidth?: boolean;
    briefId?: string;
    removeBottomMargin?: boolean;

    workEntity?: WorkEntityDisplay;
}

/**
 * Returns hardcoded agreement based on expert service overflow eligibility
 * This is a fallback for legacy requests that do not have agreement data
 */
function agreementForRequest(request: BriefRequestFragment | undefined): Agreement {
    const standard: Agreement = {
        payDeadline: 7,
        requestDeadline: 30,
        deliverables: 1,
        revisions: 5,
        turnaround: 3,
        deliverableType: __('unique design concept', {
            _context:
                'Displayed to freelance designers in the design service statement of work and specifies what they must deliver to the customer',
        }),
    };

    const isSwiftProduct = request?.isOverflowEligible;
    if (!isSwiftProduct) {
        return standard;
    }

    const fallbackSwiftAgreement: Agreement = {
        ...standard,
        revisions: 3,
        turnaround: 1,
        deliverableType: __('design', {
            _context:
                'Displayed to freelance designers in the design service statement of work and specifies what they must deliver to the customer',
        }),
    };

    return fallbackSwiftAgreement;
}

/**
 * Checks if the request is for a Logo Touch Up product
 */
function isLogoTouchUpRequest(request?: BriefRequestFragment): boolean {
    const mpvId = request?.expertService?.serviceProduct?.mpvId?.toLowerCase();
    return mpvId === 'prologotouchupandcompletion';
}

export function ProductCard({
    product: baseProduct,
    request,
    withProductModal,
    hideImage,
    isFreelanceDesigner,
    matchedDate,
    fullWidth,
    briefId,
    removeBottomMargin,
}: ProductCardProps) {
    const screenClass = useScreenClass();
    const isMobile = screenClass === 'xs';
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSOWModalOpen, setIsSOWModalOpen] = useState(false);
    const product = baseProduct ?? expertServiceToProduct(request, INCLUDE_TAX);

    if (!product) {
        return null;
    }

    const DesignerEarningsBlock = () => (
        <FlexBox marginTop="5" flexDirection="column">
            <Divider />
            <FlexBox gap="5" justifyContent="flex-end" style={{ width: '100%' }}>
                {product.pricing.earningsInUsd && (
                    <Box textAlign="right">
                        <ProductPrice
                            amountInCents={product.pricing.earningsInUsd}
                            currency="USD"
                            decimalPlaces={2}
                        />
                        <Typography fontSkin="body-small-bold" textColor="subtle">
                            {__('Your earnings')}
                        </Typography>
                    </Box>
                )}
            </FlexBox>
        </FlexBox>
    );

    const isLogoTouchUp = isLogoTouchUpRequest(request);

    const agreement = request?.expertService?.agreement || agreementForRequest(request);

    return (
        <>
            <Card
                bordered
                p={'7'}
                {...(removeBottomMargin ? {} : { mb: 'between-sections' })}
                style={{ minHeight: '224px' }}
            >
                <FlexBox
                    gap={'7'}
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap-reverse"
                >
                    <Box style={{ width: isMobile || fullWidth ? '100%' : '70%' }}>
                        <Typography fontSize="large" fontWeight="bold">
                            {product.title}
                        </Typography>
                        <Ul mt={'4'}>
                            {product.details.map((item) => (
                                <Li pb={0} key={item.title}>
                                    {item.title}
                                </Li>
                            ))}
                        </Ul>
                        {isFreelanceDesigner && (
                            <Link
                                mt={'4'}
                                fontSkin={'body-small'}
                                onClick={() => setIsSOWModalOpen(true)}
                            >
                                {__('View Statement of Work')}
                            </Link>
                        )}
                        {withProductModal && (
                            <ProductModal
                                isOpen={isModalOpen}
                                setIsOpen={setIsModalOpen}
                                product={product}
                            />
                        )}

                        {isFreelanceDesigner ? (
                            <DesignerEarningsBlock />
                        ) : (
                            <ProductPrice
                                amountInCents={product.pricing.amount.amountInCents}
                                currency={product.pricing.amount.currency}
                                decimalPlaces={2}
                            />
                        )}
                    </Box>
                    {product.fulfilmentMpvId && !hideImage && (
                        <ProductImage
                            fulfilmentMpvId={product.fulfilmentMpvId}
                            title={product.title}
                        />
                    )}
                </FlexBox>
            </Card>
            {isFreelanceDesigner && briefId && (
                <SOWModal
                    isOpen={isSOWModalOpen}
                    setIsOpen={setIsSOWModalOpen}
                    matchedDate={matchedDate}
                    earnings={product.pricing.earningsInUsd}
                    requestId={request ? request.id : briefId}
                    agreement={agreement}
                    isLogoTouchUp={isLogoTouchUp}
                />
            )}
        </>
    );
}

export function ProductPrice({
    amountInCents,
    currency,
    showFixedPrice,
    decimalPlaces,
}: {
    amountInCents: number;
    currency: string;
    showFixedPrice?: boolean;
    decimalPlaces?: number;
}) {
    return (
        <Typography fontSize="large" fontWeight="bold" mt={'5'} mb={0}>
            {price({
                amount: amountInCents,
                currency: currency as Currency,
                decimalPlaces: decimalPlaces,
            })}
            {showFixedPrice && (
                <Typography fontSize="xsmall" fontWeight="normal" ml="2" as="span">
                    {__('Fixed price')}
                </Typography>
            )}
        </Typography>
    );
}

function ProductImage({ fulfilmentMpvId, title }: { fulfilmentMpvId: string; title: string }) {
    const { data, loading, error } = useProductImageUrlQuery({
        variables: {
            mpvID: fulfilmentMpvId,
        },
    });

    if (error) {
        bugtracker.notify(new Error(`Error fetching product image URL: ${error.message}.`));
        return null;
    }

    if (!data || loading) {
        return <Spinner marginRight={'7'} size="super" accessibleText="Loading..." />;
    }

    if (!data.productImageUrl || data.productImageUrl.trim() === '') {
        return null;
    }

    return (
        <FluidImage
            style={{ maxHeight: '160px', maxWidth: '160px' }}
            src={data.productImageUrl}
            alt={title}
        />
    );
}
